import { Archive, ArrowRight, Coffee, HelpCircle, Heart, Sparkles, User } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { NEWS_ITEMS } from '../lib/news';

type ContentPage = 'news' | 'you' | 'about';

const EXPLORE: {
  id: ContentPage | 'how-to-play' | 'archive';
  to: string;
  label: string;
  hint: string;
  icon: typeof Archive;
}[] = [
  { id: 'how-to-play', to: '/how-to-play', label: 'How to play', hint: 'Five guesses, chase #100', icon: HelpCircle },
  { id: 'archive', to: '/archive', label: 'Archive', hint: 'Replay any past topic', icon: Archive },
  { id: 'news', to: '/news', label: 'News', hint: 'What’s new in the game', icon: Sparkles },
  { id: 'you', to: '/you', label: 'You', hint: 'Medals and streaks', icon: User },
  { id: 'about', to: '/about', label: 'About us', hint: 'The story behind Centumth', icon: Heart },
];

export function ContentLayout({
  current,
  children,
}: {
  current: ContentPage;
  children: ReactNode;
}) {
  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,42rem)_17.5rem] lg:justify-center lg:gap-8 xl:gap-10 lg:items-start">
      <div className="min-w-0">{children}</div>
      <aside className="hidden lg:block">
        <div className="sticky top-4">
          <PageSidebar current={current} />
        </div>
      </aside>
    </div>
  );
}

export function PageSidebar({ current }: { current: ContentPage }) {
  const latest = NEWS_ITEMS[0];
  const links = EXPLORE.filter((item) => item.id !== current);

  return (
    <div className="space-y-3">
      <Link
        to="/play"
        className="group relative block overflow-hidden rounded-3xl bg-foreground text-background px-5 py-5 hover:opacity-90 transition-opacity"
      >
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(to_right,#fb923c,#facc15,#4ade80,#22d3ee)]"
        />
        <div className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 mb-2">
          Daily puzzle
        </div>
        <div className="text-xl font-black tracking-tight leading-tight">Play today</div>
        <p className="text-sm opacity-65 mt-1 leading-relaxed">Five guesses. Chase the hidden gems.</p>
        <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold">
          Start
          <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
        </div>
      </Link>

      <nav className="rounded-3xl border border-border bg-background/60 p-2">
        {links.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 hover:bg-muted transition-colors"
            >
              <div className="shrink-0 w-8 h-8 rounded-xl bg-muted flex items-center justify-center text-foreground/55 group-hover:text-foreground">
                <Icon size={15} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold leading-none">{item.label}</div>
                <div className="text-[11px] text-foreground/45 mt-1 truncate">{item.hint}</div>
              </div>
            </Link>
          );
        })}
      </nav>

      {current !== 'news' && latest && (
        <Link
          to="/news"
          className="group block rounded-3xl border border-border bg-background/60 px-5 py-4 hover:bg-muted/70 hover:border-foreground/20 transition-all"
        >
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-foreground/40 mb-2">
            Latest news
          </div>
          <div className="font-bold leading-snug">{latest.title}</div>
          <div className="text-xs text-foreground/45 mt-1.5">{latest.dateLabel}</div>
        </Link>
      )}

      <a
        href="https://ko-fi.com/eliass_al"
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center gap-3 rounded-3xl border border-border bg-background/60 px-4 py-3.5 hover:bg-muted/70 hover:border-foreground/20 transition-all"
      >
        <div className="shrink-0 w-8 h-8 rounded-xl bg-muted flex items-center justify-center">
          <Coffee size={15} className="text-foreground/55" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold">Support the game</div>
          <div className="text-[11px] text-foreground/45">A coffee keeps the lists coming</div>
        </div>
      </a>
    </div>
  );
}
